"use client";

import { useTranslations } from "use-intl";

import { AlbumGrid } from "@/components/album-grid";
import { type LinkTarget } from "@/components/link";
import { TrackingScope } from "@/components/TrackingScope";
import type { ListingMap } from "@/hooks/use-listing-counts";
import type { RouterOutputs } from "@/types/rpc";

type Album = RouterOutputs["music"]["searchAlbums"]["albums"][number];

/**
 * Album results section. Owns its own loading spinner, inline error, and
 * empty-collapse, plus prev/next pagination — independent of the artist leg.
 */
export function AlbumResults({
  albums,
  isLoading,
  error,
  listingMap,
  buildAlbumLink,
  hasMore,
  page,
  updateFilter,
}: {
  albums: Album[];
  isLoading: boolean;
  error?: string;
  listingMap: ListingMap;
  buildAlbumLink: (album: Album) => LinkTarget;
  hasMore: boolean;
  page: number;
  updateFilter: (key: string, value: string | number | boolean | undefined) => void;
}) {
  const t = useTranslations("globals.search");
  const tCommon = useTranslations("globals.common");

  if (error) {
    return (
      <section className="mb-12">
        <p className="text-sm text-muted-foreground py-6">{tCommon("error", { message: error })}</p>
      </section>
    );
  }

  if (!isLoading && albums.length === 0) return null;

  return (
    <section id="albums-results" aria-label={t("albums")} aria-busy={isLoading} className="mb-12">
      <TrackingScope surface="search">
        <AlbumGrid
          albums={albums}
          className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4"
          isLoading={isLoading}
          link={(album) => buildAlbumLink(album as Album)}
          listingMap={listingMap}
          priorityCount={2}
        >
          {(page > 1 || hasMore) && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={() => updateFilter("page", page - 1)}
                disabled={page <= 1}
                className="px-4 py-2 text-sm font-medium bg-secondary rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("previousPage")}
              </button>
              <span className="text-sm text-muted-foreground">{t("page", { page })}</span>
              <button
                onClick={() => updateFilter("page", page + 1)}
                disabled={!hasMore}
                className="px-4 py-2 text-sm font-medium bg-secondary rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("nextPage")}
              </button>
            </div>
          )}
        </AlbumGrid>
      </TrackingScope>
    </section>
  );
}
